import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { db, Routine, TrainingDay, WorkoutSession, TrainingDayExercise, Exercise } from '../database';
import { convertRoutineToShareable, generateShareableURL } from '../utils/routineSharing';
import { analyzeRoutineMuscleGroups, formatRatio } from '../utils/muscleAnalysis';
import MuscleGroupChart from '../components/MuscleGroupChart';

type TrainingDayWithExercises = TrainingDay & {
  exercises: TrainingDayExercise[];
};

const RoutineStatus: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [routine, setRoutine] = useState<Routine | null>(null);
  const [trainingDays, setTrainingDays] = useState<TrainingDayWithExercises[]>([]);
  const [workoutHistory, setWorkoutHistory] = useState<WorkoutSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState<number | null>(null);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [allTrainingDayExercises, setAllTrainingDayExercises] = useState<TrainingDayExercise[]>([]);

  const loadRoutineStatus = useCallback(async (routineId: number) => {
    try {
      setLoading(true);
      
      // Load routine
      const routineData = await db.routines.get(routineId);
      if (!routineData) {
        navigate('/routines');
        return;
      }
      setRoutine(routineData);

      // Load all training days for this routine with exercises
      const trainingDayData = await Promise.all(
        routineData.trainingDayIds.map(async dayId => {
          const trainingDay = await db.trainingDays.get(dayId);
          if (trainingDay) {
            // Load exercises for this training day from the separate table
            const trainingDayExercises = await db.trainingDayExercises
              .orderBy('order')
              .filter(exercise => exercise.trainingDayId === dayId)
              .toArray();
            
            // Merge the exercises into the training day
            return { 
              ...trainingDay, 
              exercises: trainingDayExercises 
            };
          }
          return null;
        })
      );
      setTrainingDays(trainingDayData.filter(Boolean) as TrainingDayWithExercises[]);

      // Load all exercises for exercise previews
      const allExercises = await db.exercises.toArray();
      setExercises(allExercises);

      // Load all training day exercises for muscle analysis
      const allTDExercises = await db.trainingDayExercises.toArray();
      setAllTrainingDayExercises(allTDExercises);

      // Load workout history for this routine
      const sessions = await db.workoutSessions
        .orderBy('startTime')
        .reverse()
        .filter(session => session.routineId === routineId)
        .toArray();
      setWorkoutHistory(sessions);

    } catch (error) {
      console.error('Failed to load routine status:', error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (id) {
      loadRoutineStatus(parseInt(id));
    }
  }, [id, loadRoutineStatus]);

  const startWorkout = async (trainingDayIndex: number) => {
    if (!routine) return;
    
    try {
      const trainingDayId = routine.trainingDayIds[trainingDayIndex];
      const trainingDay = await db.trainingDays.get(trainingDayId);
      if (!trainingDay) return;

      // Update routine position
      await db.routines.update(routine.id!, {
        currentDayIndex: trainingDayIndex,
        updatedAt: new Date()
      });

      const sessionId = await db.workoutSessions.add({
        routineId: routine.id!,
        trainingDayId: trainingDay.id!,
        startTime: new Date(),
        completed: false,
        createdAt: new Date()
      });

      navigate(`/workout/${sessionId}`);
    } catch (error) {
      console.error('Failed to start workout:', error);
    }
  };

  const resetRoutinePosition = async () => {
    if (!routine) return;
    
    if (window.confirm('Reset routine to the beginning? This will set your position back to day 1.')) {
      try {
        await db.routines.update(routine.id!, {
          currentDayIndex: 0,
          updatedAt: new Date()
        });
        await loadRoutineStatus(routine.id!);
      } catch (error) {
        console.error('Failed to reset routine:', error);
      }
    }
  };

  const toggleActive = async () => {
    if (!routine) return;
    
    try {
      await db.routines.update(routine.id!, {
        active: !routine.active,
        updatedAt: new Date()
      });
      await loadRoutineStatus(routine.id!);
    } catch (error) {
      console.error('Failed to update routine status:', error);
    }
  };

  const archiveRoutine = async () => {
    if (!routine) return;
    
    if (window.confirm('Archive this routine? You can restore it later from the Training page.')) {
      try {
        await db.routines.update(routine.id!, {
          archived: true,
          active: false,
          updatedAt: new Date()
        });
        navigate('/');
      } catch (error) {
        console.error('Failed to archive routine:', error);
      }
    }
  };

  const restoreRoutine = async () => {
    if (!routine) return;
    
    try {
      await db.routines.update(routine.id!, {
        archived: false,
        updatedAt: new Date()
      });
      await loadRoutineStatus(routine.id!);
    } catch (error) {
      console.error('Failed to restore routine:', error);
    }
  };

  const getTrainingDayStatus = (dayIndex: number) => {
    if (!routine) return 'unknown';
    
    if (dayIndex === routine.currentDayIndex) {
      return 'current';
    } else if (dayIndex < routine.currentDayIndex) {
      return 'completed';
    } else {
      return 'upcoming';
    }
  };

  const getLastWorkoutForDay = (trainingDayId: number) => {
    return workoutHistory
      .filter(session => session.trainingDayId === trainingDayId && session.completed)
      .slice(0, 1)[0];
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const getExerciseName = (exerciseId: number) => {
    return exercises.find(ex => ex.id === exerciseId)?.name || 'Unknown Exercise';
  };

  const toggleDayExpansion = (dayIndex: number) => {
    setExpandedDay(expandedDay === dayIndex ? null : dayIndex);
  };

  const navigateToSummary = (sessionId: number) => {
    navigate(`/summary/${sessionId}`);
  };

  // Fallback clipboard methods for when modern clipboard API isn't available
  const copyToClipboardFallback = (text: string) => {
    // Create temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      if (successful) {
        alert('Share link copied to clipboard!');
      } else {
        showShareUrlDialog(text);
      }
    } catch (err) {
      console.warn('execCommand copy failed:', err);
      showShareUrlDialog(text);
    } finally {
      document.body.removeChild(textArea);
    }
  };

  const showShareUrlDialog = (shareUrl: string) => {
    // Show a modal-style dialog with the URL for manual copying
    const message = `Copy this link to share your routine:\n\n${shareUrl}`;
    
    // For now, use alert - could be enhanced with a proper modal later
    alert(message);
    
    // Also log to console for easy copying in dev tools
    console.log('Share URL:', shareUrl);
  };


  const shareRoutine = async () => {
    if (!routine) return;

    setSharing(true);
    setShareError(null);

    try {
      // Collect all data needed for sharing
      const allTrainingDays = await Promise.all(
        routine.trainingDayIds.map(id => db.trainingDays.get(id))
      );
      const validTrainingDays = allTrainingDays.filter(td => td !== undefined) as TrainingDay[];

      const allTrainingDayExercises = await db.trainingDayExercises.toArray();
      const allExercises = await db.exercises.toArray();

      // Convert to shareable format
      const sharedRoutine = await convertRoutineToShareable(
        routine,
        validTrainingDays,
        allTrainingDayExercises,
        allExercises
      );

      // Always generate standard HTTP URL for sharing
      const shareUrl = generateShareableURL(sharedRoutine, false);

      // Try to use native sharing if available
      if (navigator.share) {
        await navigator.share({
          title: `Workout Routine: ${routine.name}`,
          text: `Check out this workout routine I'm sharing with you!`,
          url: shareUrl
        });
      } else {
        // Fallback strategies for copying to clipboard
        try {
          // Check if clipboard API is available and secure context
          if (navigator.clipboard && 
              typeof navigator.clipboard.writeText === 'function' && 
              (window.isSecureContext || window.location.protocol === 'https:' || window.location.hostname === 'localhost')) {
            await navigator.clipboard.writeText(shareUrl);
            alert('Share link copied to clipboard!');
          } else {
            // Fallback to legacy copy method
            copyToClipboardFallback(shareUrl);
          }
        } catch (clipboardError) {
          console.warn('Clipboard operation failed:', clipboardError);
          // Final fallback - show the URL for manual copying
          showShareUrlDialog(shareUrl);
        }
      }
    } catch (error) {
      console.error('Failed to share routine:', error);
      setShareError(error instanceof Error ? error.message : 'Failed to generate share link');
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading routine status...</div>
      </div>
    );
  }

  if (!routine) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 mb-4">Routine not found</div>
        <Link to="/routines" className="text-blue-600">← Back to Training</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-3">
        <Link to="/" className="text-blue-600 text-sm">← Back</Link>
        
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{routine.name}</h1>
          <div className="flex items-center gap-3 mt-1">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              routine.active 
                ? 'bg-green-100 text-green-800' 
                : 'bg-gray-100 text-gray-600'
            }`}>
              {routine.active ? 'Active' : 'Inactive'}
            </span>
            {routine.archived && (
              <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs font-medium">
                Archived
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {!routine.archived && (
            <button
              onClick={toggleActive}
              className={`flex-1 px-3 py-2 rounded text-sm font-medium ${
                routine.active 
                  ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' 
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {routine.active ? 'Deactivate' : 'Activate'}
            </button>
          )}
          <Link
            to={`/routines/${routine.id}/edit`}
            className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded text-sm hover:bg-gray-200 text-center"
          >
            Edit Plan
          </Link>
          <button
            onClick={shareRoutine}
            disabled={sharing}
            className="flex-1 bg-purple-100 text-purple-700 px-3 py-2 rounded text-sm hover:bg-purple-200 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
          >
            {sharing ? '...' : '🔗 Share'}
          </button>
          <button
            onClick={resetRoutinePosition}
            className="flex-1 bg-blue-100 text-blue-700 px-3 py-2 rounded text-sm hover:bg-blue-200"
          >
            Reset Position
          </button>
          {routine.archived && (
            <button
              onClick={restoreRoutine}
              className="flex-1 bg-green-100 text-green-700 px-3 py-2 rounded text-sm hover:bg-green-200"
            >
              Restore
            </button>
          )}
        </div>

        {/* Share Error Display */}
        {shareError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="text-red-800 text-sm">
              <strong>Share failed:</strong> {shareError}
            </div>
          </div>
        )}
      </div>

      {/* Cycle Overview */}
      <div className="bg-white border rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Training Cycle</h2>
          <div className="flex items-center gap-1">
            {routine.trainingDayIds.map((_, index) => (
              <div
                key={index}
                className={`w-3 h-3 rounded-full ${
                  index === routine.currentDayIndex 
                    ? 'bg-blue-500' 
                    : index < routine.currentDayIndex
                    ? 'bg-green-500'
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="space-y-3">
          {trainingDays.map((trainingDay, index) => {
            const status = getTrainingDayStatus(index);
            const lastWorkout = getLastWorkoutForDay(trainingDay.id!);
            const isExpanded = expandedDay === index;
            
            return (
              <div
                key={trainingDay.id}
                className={`border rounded-lg ${
                  status === 'current' 
                    ? 'border-blue-300 bg-blue-50' 
                    : status === 'completed'
                    ? 'border-green-300 bg-green-50'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="p-4">
                  <div className="space-y-3">
                    {/* Header with title and badge */}
                    <div className="flex items-start justify-between">
                      <h3 className={`text-lg font-semibold ${
                        status === 'current' ? 'text-blue-900' :
                        status === 'completed' ? 'text-green-900' :
                        'text-gray-700'
                      }`}>
                        Day {index + 1}: {trainingDay.name}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        status === 'current' 
                          ? 'bg-blue-200 text-blue-800'
                          : status === 'completed'
                          ? 'bg-green-200 text-green-800'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {status === 'current' ? 'Next Up' : 
                         status === 'completed' ? 'In Cycle' :
                         'Upcoming'}
                      </span>
                    </div>

                    {/* Exercise count and last workout */}
                    <div className="text-sm text-gray-600">
                      {trainingDay.exercises.length} exercises
                      {lastWorkout && (
                        <button
                          onClick={() => navigateToSummary(lastWorkout.id!)}
                          className="ml-2 text-blue-600 hover:text-blue-800 underline"
                        >
                          • Last: {formatTimeAgo(lastWorkout.startTime)}
                        </button>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center justify-end gap-2">
                      {(status === 'upcoming' || status === 'current') && (
                        <button
                          onClick={() => toggleDayExpansion(index)}
                          className="bg-gray-100 text-gray-600 px-3 py-2 rounded text-sm hover:bg-gray-200 flex items-center gap-1"
                        >
                          <span>See More</span>
                          <svg 
                            className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      )}
                      {status === 'current' && (
                        <button
                          onClick={() => startWorkout(index)}
                          className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 flex items-center gap-1"
                        >
                          <span>Start</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      )}
                      {status !== 'current' && status !== 'upcoming' && (
                        <button
                          onClick={() => startWorkout(index)}
                          className="bg-gray-100 text-gray-600 px-4 py-2 rounded text-sm hover:bg-gray-200"
                        >
                          Jump Here
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expandable Exercise Preview */}
                {isExpanded && (status === 'upcoming' || status === 'current') && (
                  <div className="border-t bg-white/50 p-4">
                    <h4 className="font-medium text-gray-900 mb-3">
                      {status === 'current' ? 'Next Workout' : 'Exercise Preview'}
                    </h4>
                    <div className="space-y-2">
                      {trainingDay.exercises.slice(0, 5).map((exercise, exerciseIndex) => (
                        <div key={exerciseIndex} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700 font-medium">
                            {getExerciseName(exercise.exerciseId)}
                          </span>
                          <span className="text-gray-500">
                            {exercise.weight}lbs • {exercise.sets.length} sets
                          </span>
                        </div>
                      ))}
                      {trainingDay.exercises.length > 5 && (
                        <div className="text-xs text-gray-500 text-center pt-2">
                          +{trainingDay.exercises.length - 5} more exercises
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Muscle Group Analysis */}
      {exercises.length > 0 && trainingDays.length > 0 && (
        <div className="bg-white border rounded-xl p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Muscle Group Analysis</h2>
          
          {(() => {
            // Get training days for this routine only
            const routineTrainingDays = trainingDays.filter(td => 
              routine?.trainingDayIds.includes(td.id!)
            );
            
            // Get training day exercises for this routine only
            const routineTDExercises = allTrainingDayExercises.filter(tde => 
              routineTrainingDays.some(td => td.id === tde.trainingDayId)
            );
            
            if (routineTrainingDays.length === 0 || routineTDExercises.length === 0) {
              return (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-2xl mb-2">💪</div>
                  <p>No exercises found for analysis</p>
                  <p className="text-sm">Add exercises to your training days to see muscle group breakdown</p>
                </div>
              );
            }

            const analysis = analyzeRoutineMuscleGroups(
              routineTrainingDays,
              routineTDExercises,
              exercises
            );

            return (
              <div className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-lg font-bold text-blue-600">
                      {analysis.totalSets}
                    </div>
                    <div className="text-sm text-blue-700">Total Sets</div>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-lg font-bold text-green-600">
                      {analysis.muscleStats.length}
                    </div>
                    <div className="text-sm text-green-700">Muscle Groups</div>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <div className="text-lg font-bold text-purple-600">
                      {formatRatio(analysis.balance.pushPullRatio)}
                    </div>
                    <div className="text-sm text-purple-700">Push:Pull</div>
                  </div>
                  <div className="text-center p-3 bg-orange-50 rounded-lg">
                    <div className="text-lg font-bold text-orange-600">
                      {formatRatio(analysis.balance.upperLowerRatio)}
                    </div>
                    <div className="text-sm text-orange-700">Upper:Lower</div>
                  </div>
                </div>

                {/* Muscle Volume Chart */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Volume by Muscle Group</h3>
                  <MuscleGroupChart muscleStats={analysis.muscleStats} />
                </div>

                {/* Balance Insights */}
                {analysis.muscleStats.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">Balance Insights</h3>
                    <div className="space-y-1 text-sm text-gray-600">
                      {analysis.balance.pushPullRatio < 0.7 && (
                        <p>• Consider adding more pushing exercises for better push/pull balance</p>
                      )}
                      {analysis.balance.pushPullRatio > 1.4 && (
                        <p>• Consider adding more pulling exercises for better push/pull balance</p>
                      )}
                      {analysis.balance.upperLowerRatio > 2.5 && (
                        <p>• Consider adding more lower body volume for balanced development</p>
                      )}
                      {analysis.balance.upperLowerRatio < 1.2 && (
                        <p>• Consider adding more upper body volume for balanced development</p>
                      )}
                      {analysis.topPrimaryMuscles.length > 0 && analysis.topPrimaryMuscles[0].primarySets > analysis.totalSets * 0.3 && (
                        <p>• High focus on {analysis.topPrimaryMuscles[0].muscle} - ensure adequate recovery</p>
                      )}
                      {analysis.balance.pushPullRatio >= 0.7 && analysis.balance.pushPullRatio <= 1.4 && 
                       analysis.balance.upperLowerRatio >= 1.2 && analysis.balance.upperLowerRatio <= 2.5 && (
                        <p className="text-green-600">• Excellent muscle group balance! 🎯</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* Recent Workout History */}
      <div className="bg-white border rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Workouts</h2>
        
        {workoutHistory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <div className="text-2xl mb-2">🏃‍♂️</div>
            <p>No workouts completed yet</p>
            <p className="text-sm">Start your first workout to see progress here</p>
          </div>
        ) : (
          <div className="space-y-3">
            {workoutHistory.slice(0, 5).map((session) => {
              const trainingDay = trainingDays.find(td => td.id === session.trainingDayId);
              return (
                <button
                  key={session.id}
                  onClick={() => session.completed ? navigateToSummary(session.id!) : undefined}
                  className={`w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg text-left ${
                    session.completed ? 'hover:bg-gray-100 cursor-pointer' : 'cursor-default'
                  }`}
                  disabled={!session.completed}
                >
                  <div>
                    <div className="font-medium text-gray-900">
                      {trainingDay?.name || 'Unknown Workout'}
                    </div>
                    <div className="text-sm text-gray-600">
                      {formatTimeAgo(session.startTime)}
                      {session.duration && (
                        <span className="ml-2">• {Math.round(session.duration / 60)}min</span>
                      )}
                      {session.completed && (
                        <span className="ml-2 text-blue-600">• Click to view summary</span>
                      )}
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    session.completed 
                      ? 'bg-green-100 text-green-800'
                      : 'bg-orange-100 text-orange-800'
                  }`}>
                    {session.completed ? 'Completed' : 'In Progress'}
                  </div>
                </button>
              );
            })}
            
            {workoutHistory.length > 5 && (
              <div className="text-center pt-2">
                <Link to="/progress" className="text-blue-600 text-sm">
                  View all workout history →
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Statistics */}
      <div className="bg-white border rounded-xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Statistics</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">
              {workoutHistory.filter(s => s.completed).length}
            </div>
            <div className="text-sm text-gray-600">Workouts Completed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">
              {Math.floor(routine.currentDayIndex / routine.trainingDayIds.length)}
            </div>
            <div className="text-sm text-gray-600">Cycles Completed</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-purple-600">
              {routine.currentDayIndex + 1}
            </div>
            <div className="text-sm text-gray-600">Current Day</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">
              {workoutHistory.length > 0 ? formatTimeAgo(workoutHistory[0].startTime) : 'Never'}
            </div>
            <div className="text-sm text-gray-600">Last Workout</div>
          </div>
        </div>
      </div>

      {/* Archive Section (bottom) */}
      {!routine.archived && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-red-900 mb-2">Archive Routine</h2>
          <p className="text-sm text-red-700 mb-4">
            Archive this routine to remove it from your active training while preserving all workout history. 
            You can restore it later if needed.
          </p>
          <button
            onClick={archiveRoutine}
            className="bg-red-600 text-white px-4 py-2 rounded text-sm hover:bg-red-700"
          >
            Archive Routine
          </button>
        </div>
      )}
    </div>
  );
};

export default RoutineStatus;